import { useEffect, useState } from "react";
import { DashboardAPI } from "../api/dashboard";
import { useViewportMatch } from "../hooks/useViewportMatch";
import type {
  MarketingEmailCaptureListResponse,
  WebsiteVisitDetailResponse,
  WebsiteVisitListResponse,
} from "../types/websiteVisits";

const cardStyle: React.CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: 0,
  background: "#fff",
};

const panelStyle: React.CSSProperties = {
  ...cardStyle,
  padding: 20,
  display: "grid",
  gap: 14,
};

const mutedText: React.CSSProperties = {
  color: "#64748b",
  fontSize: 13,
  lineHeight: 1.55,
};

const responsiveGrid = (minWidth: number) =>
  `repeat(auto-fit, minmax(min(100%, ${minWidth}px), 1fr))`;

const formatInteger = (value: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
    Number(value || 0),
  );

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
};

const relativeLabel = (value?: string) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const diffMs = Date.now() - parsed.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
};

function visitorPrimaryLabel(row?: {
  authName?: string;
  cookieName?: string;
  authEmail?: string;
  cookieEmail?: string;
  authPhone?: string;
  cookiePhone?: string;
  visitorId?: string;
  cookieVisitorId?: string;
  visitorKey?: string;
}) {
  const candidates = [
    row?.authName,
    row?.cookieName,
    row?.authEmail,
    row?.cookieEmail,
    row?.authPhone,
    row?.cookiePhone,
    row?.visitorId,
    row?.cookieVisitorId,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  if (candidates.length) return candidates[0];
  return String(row?.visitorKey || "")
    .replace(/^auth:|^visitor:|^cookie:|^fingerprint:/, "")
    .trim();
}

function visitorSecondaryLabel(row?: {
  authName?: string;
  cookieName?: string;
  authEmail?: string;
  cookieEmail?: string;
  authPhone?: string;
  cookiePhone?: string;
  authUid?: string;
  latestPlatform?: string;
  latestTimezone?: string;
  latestLanguage?: string;
}) {
  const primary = visitorPrimaryLabel(row);
  const details = [
    row?.authEmail,
    row?.cookieEmail,
    row?.authPhone,
    row?.cookiePhone,
    row?.authUid ? `UID ${row.authUid}` : "",
    row?.latestPlatform,
    row?.latestTimezone,
    row?.latestLanguage,
  ]
    .map((value) => String(value || "").trim())
    .filter((value) => value && value !== primary);
  return details.join(" • ");
}

function visitorIdentityStatus(row?: {
  isAuthenticated?: boolean;
  cookieName?: string;
  cookieEmail?: string;
  cookiePhone?: string;
}) {
  if (row?.isAuthenticated) return "Logged-in visitor";
  if (row?.cookieName || row?.cookieEmail || row?.cookiePhone) {
    return "Cookie-identified guest";
  }
  return "Guest visitor";
}

function sourcePrimaryLabel(row?: {
  latestSourceLabel?: string;
  sourceLabel?: string;
  latestTrafficChannel?: string;
  trafficChannel?: string;
  latestTrafficSource?: string;
  trafficSource?: string;
}) {
  return (
    row?.latestSourceLabel ||
    row?.sourceLabel ||
    row?.latestTrafficChannel ||
    row?.trafficChannel ||
    row?.latestTrafficSource ||
    row?.trafficSource ||
    "Unknown source"
  );
}

function sourceSecondaryLabel(row?: {
  latestTrafficChannel?: string;
  trafficChannel?: string;
  latestTrafficMedium?: string;
  trafficMedium?: string;
  latestTrafficKeyword?: string;
  trafficKeyword?: string;
  latestUtmCampaign?: string;
  utmCampaign?: string;
  latestReferrerHost?: string;
  referrerHost?: string;
  latestAttributionReason?: string;
  attributionReason?: string;
}) {
  return [
    row?.latestTrafficChannel || row?.trafficChannel,
    row?.latestTrafficMedium || row?.trafficMedium,
    row?.latestTrafficKeyword || row?.trafficKeyword
      ? `keyword: ${row?.latestTrafficKeyword || row?.trafficKeyword}`
      : "",
    row?.latestUtmCampaign || row?.utmCampaign
      ? `campaign: ${row?.latestUtmCampaign || row?.utmCampaign}`
      : "",
    row?.latestReferrerHost || row?.referrerHost,
    row?.latestAttributionReason || row?.attributionReason,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" • ");
}

function SectionHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <h1 style={{ margin: 0, fontSize: 26, color: "#0f172a" }}>{title}</h1>
      {subtitle ? <div style={mutedText}>{subtitle}</div> : null}
    </div>
  );
}

function MetricCard({
  title,
  value,
  meta,
}: {
  title: string;
  value: string;
  meta?: string;
}) {
  return (
    <div
      style={{
        ...cardStyle,
        padding: 18,
        display: "grid",
        gap: 6,
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.2,
          textTransform: "uppercase",
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a" }}>
        {value}
      </div>
      {meta ? <div style={mutedText}>{meta}</div> : null}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div
      style={{
        border: "1px dashed #cbd5e1",
        borderRadius: 0,
        padding: "16px 14px",
        color: "#64748b",
        fontSize: 13,
        background: "#f8fafc",
      }}
    >
      {text}
    </div>
  );
}

function Badge({
  label,
  tone,
}: {
  label: string;
  tone: "dark" | "light" | "warn";
}) {
  const palette =
    tone === "dark"
      ? {
          border: "#cbd5e1",
          background: "#f8fafc",
          color: "#0f172a",
        }
      : tone === "warn"
        ? {
            border: "#fcd34d",
            background: "#fffbeb",
            color: "#92400e",
          }
        : {
            border: "#dbeafe",
            background: "#eff6ff",
            color: "#1d4ed8",
          };

  return (
    <span
      style={{
        border: `1px solid ${palette.border}`,
        background: palette.background,
        color: palette.color,
        borderRadius: 0,
        padding: "5px 9px",
        fontSize: 11,
        fontWeight: 800,
      }}
    >
      {label}
    </span>
  );
}

function WebsiteVisitsTable({
  data,
  loading,
  error,
  selectedVisitorKey,
  search,
  identityFilter,
  onSearchChange,
  onIdentityFilterChange,
  onSelectVisitor,
  onPageChange,
}: {
  data: WebsiteVisitListResponse | null;
  loading: boolean;
  error: string | null;
  selectedVisitorKey: string;
  search: string;
  identityFilter: string;
  onSearchChange: (value: string) => void;
  onIdentityFilterChange: (value: string) => void;
  onSelectVisitor: (visitorKey: string) => void;
  onPageChange: (page: number) => void;
}) {
  if (error && !data) {
    return (
      <div
        style={{
          borderRadius: 0,
          border: "1px solid #fecdd3",
          background: "#fff1f2",
          color: "#9f1239",
          padding: "12px 14px",
        }}
      >
        {error}
      </div>
    );
  }

  const summary = data?.summary;
  const rows = data?.items || [];
  const filteredRows = rows.filter((row) => {
    const normalizedSearch = search.trim().toLowerCase();
    const matchesSearch =
      !normalizedSearch ||
      [
        visitorPrimaryLabel(row),
        visitorSecondaryLabel(row),
        sourcePrimaryLabel(row),
        sourceSecondaryLabel(row),
        row.latestPath,
        row.latestPageTitle,
        row.latestLocation,
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch);
    const matchesIdentity =
      identityFilter === "all" ||
      (identityFilter === "identified" &&
        (row.isAuthenticated ||
          Boolean(row.authEmail || row.cookieEmail || row.authPhone || row.cookiePhone))) ||
      (identityFilter === "anonymous" &&
        !row.isAuthenticated &&
        !row.authEmail &&
        !row.cookieEmail &&
        !row.authPhone &&
        !row.cookiePhone);
    return matchesSearch && matchesIdentity;
  });

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div
        style={{
          display: "grid",
          gap: 10,
          gridTemplateColumns: responsiveGrid(140),
        }}
      >
        <MetricCard
          title="Visitors"
          value={formatInteger(summary?.totalVisitors || 0)}
          meta="Merged visitor records"
        />
        <MetricCard
          title="Sessions"
          value={formatInteger(summary?.totalSessions || 0)}
          meta="Tracked browsing sessions"
        />
        <MetricCard
          title="Page Views"
          value={formatInteger(summary?.totalPageViews || 0)}
          meta="Recorded route changes"
        />
        <MetricCard
          title="Active 24h"
          value={formatInteger(summary?.activeVisitorsLast24Hours || 0)}
          meta="Recent visitor count"
        />
        <MetricCard
          title="Identified"
          value={formatInteger(summary?.identifiedVisitors || 0)}
          meta={`${formatInteger(summary?.anonymousVisitors || 0)} anonymous`}
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
          gap: 10,
        }}
      >
        {[
          {
            title: "Top Pages",
            rows: (summary?.topPages || []).map((row) => ({
              label: row.path,
              value: `${formatInteger(row.views)} views / ${formatInteger(row.visitors)} visitors`,
            })),
          },
          {
            title: "Top Sources",
            rows: (summary?.topSources || []).map((row) => ({
              label: row.source,
              value: `${formatInteger(row.views)} views / ${formatInteger(row.visitors)} visitors`,
            })),
          },
        ].map((block) => (
          <div
            key={block.title}
            style={{
              border: "1px solid #e2e8f0",
              background: "#fff",
              padding: 14,
              display: "grid",
              gap: 8,
            }}
          >
            <div
              style={{
                color: "#475569",
                fontSize: 12,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: ".08em",
              }}
            >
              {block.title}
            </div>
            {(block.rows.length ? block.rows : [{ label: "No data yet", value: "" }]).map(
              (row) => (
                <div
                  key={`${block.title}-${row.label}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    borderTop: "1px solid #f1f5f9",
                    paddingTop: 8,
                    fontSize: 13,
                  }}
                >
                  <span style={{ color: "#0f172a", fontWeight: 800 }}>{row.label}</span>
                  <span style={{ color: "#64748b", whiteSpace: "nowrap" }}>
                    {row.value}
                  </span>
                </div>
              ),
            )}
          </div>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 180px",
          gap: 10,
        }}
      >
        <input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search visitor, email, source, page, location"
          style={{
            border: "1px solid #cbd5e1",
            borderRadius: 0,
            padding: "10px 12px",
            minHeight: 40,
          }}
        />
        <select
          value={identityFilter}
          onChange={(event) => onIdentityFilterChange(event.target.value)}
          style={{
            border: "1px solid #cbd5e1",
            borderRadius: 0,
            padding: "10px 12px",
            minHeight: 40,
            background: "#fff",
          }}
        >
          <option value="all">All visitors</option>
          <option value="identified">Identified</option>
          <option value="anonymous">Anonymous</option>
        </select>
      </div>

      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: 0,
          overflow: "hidden",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: 1120,
            }}
          >
            <thead style={{ background: "#f8fafc" }}>
              <tr>
                {[
                  "Visitor",
                  "Identity",
                  "Source",
                  "Location",
                  "Activity",
                  "Latest Page",
                  "Last Seen",
                ].map(
                  (label) => (
                    <th
                      key={label}
                      style={{
                        textAlign: "left",
                        padding: "12px 14px",
                        fontSize: 12,
                        color: "#475569",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      {label}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => {
                const active = row.visitorKey === selectedVisitorKey;
                return (
                  <tr
                    key={row.visitorKey}
                    onClick={() => onSelectVisitor(row.visitorKey)}
                    style={{
                      cursor: "pointer",
                      background: active ? "rgba(14,165,233,.08)" : "#fff",
                    }}
                  >
                    <td
                      style={{
                        padding: "12px 14px",
                        borderBottom: "1px solid #eef2f7",
                        verticalAlign: "top",
                      }}
                    >
                      <div style={{ fontWeight: 800, color: "#0f172a" }}>
                        {visitorPrimaryLabel(row)}
                      </div>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                        {row.deviceTypes.length
                          ? row.deviceTypes.join(" • ")
                          : "Unknown device"}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "12px 14px",
                        borderBottom: "1px solid #eef2f7",
                        verticalAlign: "top",
                      }}
                    >
                      <div style={{ color: "#0f172a", fontWeight: 700 }}>
                        {visitorIdentityStatus(row)}
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                        {visitorSecondaryLabel(row) || "No extra identity captured"}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "12px 14px",
                        borderBottom: "1px solid #eef2f7",
                        verticalAlign: "top",
                      }}
                    >
                      <div style={{ color: "#0f172a", fontWeight: 800 }}>
                        {sourcePrimaryLabel(row)}
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                        {sourceSecondaryLabel(row) || "No attribution details"}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "12px 14px",
                        borderBottom: "1px solid #eef2f7",
                        verticalAlign: "top",
                        color: "#0f172a",
                      }}
                    >
                      <div>{row.latestLocation || "Unknown"}</div>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                        {row.latestIpAddress || "Unknown IP"}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "12px 14px",
                        borderBottom: "1px solid #eef2f7",
                        verticalAlign: "top",
                      }}
                    >
                      <div style={{ fontWeight: 800, color: "#0f172a" }}>
                        {formatInteger(row.pageViewCount)}
                      </div>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                        {formatInteger(row.sessionCount)} sessions •{" "}
                        {formatInteger(row.uniquePageCount)} unique pages
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "12px 14px",
                        borderBottom: "1px solid #eef2f7",
                        verticalAlign: "top",
                      }}
                    >
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>
                        {row.latestPageTitle || row.latestPath}
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                        {row.latestPath}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "12px 14px",
                        borderBottom: "1px solid #eef2f7",
                        verticalAlign: "top",
                      }}
                    >
                      <div style={{ color: "#0f172a", fontWeight: 700 }}>
                        {formatDateTime(row.lastSeenAt)}
                      </div>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                        {relativeLabel(row.lastSeenAt)}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!filteredRows.length ? (
                <tr>
                  <td colSpan={7} style={{ padding: "16px 14px", color: "#64748b" }}>
                    {loading
                      ? "Loading website visits..."
                      : rows.length
                        ? "No visitors match the current filters."
                        : "No website visits have been tracked yet."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ color: "#64748b", fontSize: 12 }}>
          {formatInteger(data?.total || 0)} total visitors • page {data?.page || 1} of{" "}
          {data?.pages || 1}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, (data?.page || 1) - 1))}
            disabled={!data || (data.page || 1) <= 1}
            style={{
              padding: "8px 12px",
              borderRadius: 0,
              border: "1px solid #e5e7eb",
              background: !data || (data.page || 1) <= 1 ? "#f8fafc" : "#fff",
              cursor: !data || (data.page || 1) <= 1 ? "default" : "pointer",
            }}
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() =>
              onPageChange(Math.min(data?.pages || 1, (data?.page || 1) + 1))
            }
            disabled={!data || (data.page || 1) >= (data.pages || 1)}
            style={{
              padding: "8px 12px",
              borderRadius: 0,
              border: "1px solid #e5e7eb",
              background:
                !data || (data.page || 1) >= (data.pages || 1) ? "#f8fafc" : "#fff",
              cursor:
                !data || (data.page || 1) >= (data.pages || 1) ? "default" : "pointer",
            }}
          >
            Next
          </button>
        </div>
      </div>
      {error ? <div style={{ color: "#b91c1c", fontSize: 13 }}>{error}</div> : null}
    </div>
  );
}

function WebsiteVisitDetailPanel({
  detail,
  loading,
  error,
}: {
  detail: WebsiteVisitDetailResponse | null;
  loading: boolean;
  error: string | null;
}) {
  if (error && !detail) {
    return (
      <div
        style={{
          borderRadius: 0,
          border: "1px solid #fecdd3",
          background: "#fff1f2",
          color: "#9f1239",
          padding: "12px 14px",
        }}
      >
        {error}
      </div>
    );
  }

  if (!detail) {
    return (
      <EmptyState
        text={
          loading
            ? "Loading visitor detail..."
            : "Select a visitor to inspect sessions and page history."
        }
      />
    );
  }

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div
        style={{
          display: "grid",
          gap: 10,
          gridTemplateColumns: responsiveGrid(150),
        }}
      >
        <MetricCard
          title="Sessions"
          value={formatInteger(detail.visitor.sessionCount)}
          meta="Merged browsing sessions"
        />
        <MetricCard
          title="Page Views"
          value={formatInteger(detail.visitor.pageViewCount)}
          meta="Tracked route changes"
        />
        <MetricCard
          title="Known IPs"
          value={formatInteger(detail.visitor.knownIpAddresses.length)}
          meta="Observed network addresses"
        />
        <MetricCard
          title="Locations"
          value={formatInteger(detail.visitor.knownLocations.length)}
          meta="Observed location labels"
        />
        <MetricCard
          title="Sources"
          value={formatInteger(detail.visitor.knownSources.length)}
          meta="Observed traffic sources"
        />
      </div>

      <div style={{ ...cardStyle, padding: 16, display: "grid", gap: 8 }}>
        <div style={{ fontWeight: 800, color: "#0f172a" }}>
          {visitorPrimaryLabel(detail.visitor)}
        </div>
        <div style={{ fontSize: 13, color: "#334155" }}>
          <strong>Identity:</strong> {visitorIdentityStatus(detail.visitor)}
        </div>
        {visitorSecondaryLabel(detail.visitor) ? (
          <div style={{ fontSize: 13, color: "#334155" }}>
            <strong>Captured details:</strong> {visitorSecondaryLabel(detail.visitor)}
          </div>
        ) : null}
        {detail.visitor.cookieName ? (
          <div style={{ fontSize: 13, color: "#334155" }}>
            <strong>Cookie name:</strong> {detail.visitor.cookieName}
          </div>
        ) : null}
        {detail.visitor.cookieEmail ? (
          <div style={{ fontSize: 13, color: "#334155" }}>
            <strong>Cookie email:</strong> {detail.visitor.cookieEmail}
          </div>
        ) : null}
        {detail.visitor.cookiePhone ? (
          <div style={{ fontSize: 13, color: "#334155" }}>
            <strong>Cookie phone:</strong> {detail.visitor.cookiePhone}
          </div>
        ) : null}
        <div style={{ fontSize: 13, color: "#334155" }}>
          <strong>Latest page:</strong>{" "}
          {detail.visitor.latestPageTitle || detail.visitor.latestPath}
        </div>
        <div style={{ fontSize: 13, color: "#334155" }}>
          <strong>Latest path:</strong> {detail.visitor.latestPath}
        </div>
        <div style={{ fontSize: 13, color: "#334155" }}>
          <strong>Latest IP:</strong> {detail.visitor.latestIpAddress || "Unknown"}
        </div>
        <div style={{ fontSize: 13, color: "#334155" }}>
          <strong>Latest location:</strong>{" "}
          {detail.visitor.latestLocation || "Unknown"}
        </div>
        <div style={{ fontSize: 13, color: "#334155" }}>
          <strong>Latest source:</strong> {sourcePrimaryLabel(detail.visitor)}
        </div>
        {sourceSecondaryLabel(detail.visitor) ? (
          <div style={{ fontSize: 13, color: "#334155" }}>
            <strong>Source details:</strong> {sourceSecondaryLabel(detail.visitor)}
          </div>
        ) : null}
        <div style={{ fontSize: 13, color: "#334155" }}>
          <strong>Seen:</strong> {formatDateTime(detail.visitor.firstSeenAt)} to{" "}
          {formatDateTime(detail.visitor.lastSeenAt)}
        </div>
        <div style={{ fontSize: 13, color: "#334155" }}>
          <strong>Devices:</strong>{" "}
          {detail.visitor.deviceTypes.length
            ? detail.visitor.deviceTypes.join(" • ")
            : "Unknown"}
        </div>
        {detail.visitor.latestPlatform ? (
          <div style={{ fontSize: 13, color: "#334155" }}>
            <strong>Platform:</strong> {detail.visitor.latestPlatform}
          </div>
        ) : null}
        {detail.visitor.latestTimezone ? (
          <div style={{ fontSize: 13, color: "#334155" }}>
            <strong>Timezone:</strong> {detail.visitor.latestTimezone}
          </div>
        ) : null}
        {detail.visitor.latestLanguage ? (
          <div style={{ fontSize: 13, color: "#334155" }}>
            <strong>Language:</strong> {detail.visitor.latestLanguage}
          </div>
        ) : null}
        {detail.visitor.knownCookieVisitorIds.length ? (
          <div style={{ fontSize: 13, color: "#334155" }}>
            <strong>Cookie IDs:</strong>{" "}
            {detail.visitor.knownCookieVisitorIds.join(", ")}
          </div>
        ) : null}
        {detail.visitor.knownIpAddresses.length ? (
          <div style={{ fontSize: 13, color: "#334155" }}>
            <strong>All IPs:</strong> {detail.visitor.knownIpAddresses.join(", ")}
          </div>
        ) : null}
        {detail.visitor.knownLocations.length ? (
          <div style={{ fontSize: 13, color: "#334155" }}>
            <strong>All locations:</strong>{" "}
            {detail.visitor.knownLocations.join(" • ")}
          </div>
        ) : null}
        {detail.visitor.knownSources.length ? (
          <div style={{ fontSize: 13, color: "#334155" }}>
            <strong>All sources:</strong> {detail.visitor.knownSources.join(" • ")}
          </div>
        ) : null}
        {detail.visitor.knownTrafficChannels.length ? (
          <div style={{ fontSize: 13, color: "#334155" }}>
            <strong>All channels:</strong>{" "}
            {detail.visitor.knownTrafficChannels.join(" • ")}
          </div>
        ) : null}
        {detail.visitor.knownCampaigns.length ? (
          <div style={{ fontSize: 13, color: "#334155" }}>
            <strong>Campaigns:</strong> {detail.visitor.knownCampaigns.join(" • ")}
          </div>
        ) : null}
        {detail.visitor.knownKeywords.length ? (
          <div style={{ fontSize: 13, color: "#334155" }}>
            <strong>Captured keywords:</strong>{" "}
            {detail.visitor.knownKeywords.join(" • ")}
          </div>
        ) : null}
        {detail.visitor.timezones.length > 1 ? (
          <div style={{ fontSize: 13, color: "#334155" }}>
            <strong>All timezones:</strong> {detail.visitor.timezones.join(" • ")}
          </div>
        ) : null}
      </div>

      <div style={{ display: "grid", gap: 12, maxHeight: 760, overflowY: "auto" }}>
        {detail.sessions.map((session) => (
          <div
            key={session.sessionId}
            style={{
          border: "1px solid #e2e8f0",
          borderRadius: 0,
              padding: 14,
              display: "grid",
              gap: 10,
              background: "#fff",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "grid", gap: 4 }}>
                <div style={{ fontWeight: 800, color: "#0f172a" }}>
                  Session {session.sessionId}
                </div>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {formatDateTime(session.startedAt)} to{" "}
                  {formatDateTime(session.lastSeenAt)}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Badge label={`${session.pageViewCount} pages`} tone="dark" />
                {session.locations.length ? (
                  <Badge label={session.locations[0]} tone="light" />
                ) : null}
                {session.trafficChannel ? (
                  <Badge label={session.trafficChannel} tone="light" />
                ) : null}
              </div>
            </div>

            <div style={{ fontSize: 13, color: "#334155", display: "grid", gap: 4 }}>
              <div>
                <strong>Source:</strong> {sourcePrimaryLabel(session)}
              </div>
              {sourceSecondaryLabel(session) ? (
                <div>
                  <strong>Attribution:</strong> {sourceSecondaryLabel(session)}
                </div>
              ) : null}
              <div>
                <strong>Landing:</strong> {session.landingPath}
              </div>
              <div>
                <strong>Exit:</strong> {session.exitPath}
              </div>
              <div>
                <strong>External referrer:</strong>{" "}
                {session.externalReferrer || "Direct / internal"}
              </div>
              <div>
                <strong>IP addresses:</strong>{" "}
                {session.ipAddresses.length
                  ? session.ipAddresses.join(", ")
                  : "Unknown"}
              </div>
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              {session.pages.map((page) => (
                <div
                  key={page.id}
                  style={{
                    border: "1px solid #eef2f7",
                    borderRadius: 0,
                    padding: "10px 12px",
                    display: "grid",
                    gap: 4,
                    background: "#f8fafc",
                  }}
                >
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>
                    {page.pageTitle || page.path}
                  </div>
                  <div style={{ fontSize: 12, color: "#475569" }}>{page.path}</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    {formatDateTime(page.viewedAt)}
                    {page.referrerPath ? ` • from ${page.referrerPath}` : ""}
                    {page.location ? ` • ${page.location}` : ""}
                  </div>
                  {page.ipAddress ? (
                    <div style={{ fontSize: 12, color: "#64748b" }}>
                      IP: {page.ipAddress}
                    </div>
                  ) : null}
                  {page.sourceLabel || page.trafficChannel || page.trafficKeyword ? (
                    <div style={{ fontSize: 12, color: "#64748b" }}>
                      Source: {sourcePrimaryLabel(page)}
                      {sourceSecondaryLabel(page)
                        ? ` • ${sourceSecondaryLabel(page)}`
                        : ""}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {error ? <div style={{ color: "#b91c1c", fontSize: 13 }}>{error}</div> : null}
    </div>
  );
}

function EmailCapturesPanel({
  data,
  loading,
  error,
  onPageChange,
}: {
  data: MarketingEmailCaptureListResponse | null;
  loading: boolean;
  error: string | null;
  onPageChange: (page: number) => void;
}) {
  const rows = data?.items || [];
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div
        style={{
          display: "grid",
          gap: 10,
          gridTemplateColumns: responsiveGrid(170),
        }}
      >
        <MetricCard
          title="Captured Emails"
          value={formatInteger(data?.summary.totalCaptures || 0)}
          meta="Prompt submissions"
        />
        <MetricCard
          title="Converted"
          value={formatInteger(data?.summary.convertedCaptures || 0)}
          meta="Used on an order"
        />
      </div>
      <div style={{ border: "1px solid #e2e8f0", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 780 }}>
          <thead style={{ background: "#f8fafc" }}>
            <tr>
              {["Email", "Code", "Status", "Send Status", "Expires", "Order"].map((label) => (
                <th
                  key={label}
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontSize: 12,
                    color: "#475569",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7" }}>
                  <strong>{row.email}</strong>
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7" }}>
                  {row.couponCode}
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7" }}>
                  <Badge
                    label={row.status}
                    tone={row.status === "converted" ? "dark" : "light"}
                  />
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7" }}>
                  {row.emailSendError
                    ? `Failed: ${row.emailSendError}`
                    : row.emailSentAt
                      ? `Sent ${formatDateTime(row.emailSentAt)}`
                      : "Pending"}
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7" }}>
                  {formatDateTime(row.offerExpiresAt)}
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7" }}>
                  {row.convertedOrderNumber || "-"}
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr>
                <td colSpan={6} style={{ padding: "14px 12px", color: "#64748b" }}>
                  {loading ? "Loading captured emails..." : "No email captures yet."}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
        <div style={{ color: "#64748b", fontSize: 12 }}>
          {formatInteger(data?.total || 0)} captured emails • page {data?.page || 1} of{" "}
          {data?.pages || 1}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, (data?.page || 1) - 1))}
            disabled={!data || (data.page || 1) <= 1}
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => onPageChange(Math.min(data?.pages || 1, (data?.page || 1) + 1))}
            disabled={!data || (data.page || 1) >= (data.pages || 1)}
          >
            Next
          </button>
        </div>
      </div>
      {error ? <div style={{ color: "#b91c1c", fontSize: 13 }}>{error}</div> : null}
    </div>
  );
}

export default function WebsiteVisitsPage() {
  const isCompact = useViewportMatch("(max-width: 960px)");
  const [websiteVisits, setWebsiteVisits] = useState<WebsiteVisitListResponse | null>(
    null,
  );
  const [websiteVisitsLoading, setWebsiteVisitsLoading] = useState(true);
  const [websiteVisitsError, setWebsiteVisitsError] = useState<string | null>(null);
  const [websiteVisitsPage, setWebsiteVisitsPage] = useState(1);
  const [visitorSearch, setVisitorSearch] = useState("");
  const [visitorIdentityFilter, setVisitorIdentityFilter] = useState("all");
  const [selectedVisitorKey, setSelectedVisitorKey] = useState("");
  const [websiteVisitDetail, setWebsiteVisitDetail] =
    useState<WebsiteVisitDetailResponse | null>(null);
  const [websiteVisitDetailLoading, setWebsiteVisitDetailLoading] = useState(false);
  const [websiteVisitDetailError, setWebsiteVisitDetailError] =
    useState<string | null>(null);
  const [emailCaptures, setEmailCaptures] =
    useState<MarketingEmailCaptureListResponse | null>(null);
  const [emailCapturesLoading, setEmailCapturesLoading] = useState(true);
  const [emailCapturesError, setEmailCapturesError] = useState<string | null>(null);
  const [emailCapturesPage, setEmailCapturesPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setWebsiteVisitsLoading(true);
        setWebsiteVisitsError(null);
        const data = await DashboardAPI.getWebsiteVisits({
          page: websiteVisitsPage,
          limit: 20,
        });
        if (cancelled) return;
        setWebsiteVisits(data);
        setSelectedVisitorKey((current) => {
          if (current && data.items.some((item) => item.visitorKey === current)) {
            return current;
          }
          return data.items[0]?.visitorKey || "";
        });
      } catch (err: any) {
        if (cancelled) return;
        setWebsiteVisitsError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load website visits.",
        );
      } finally {
        if (!cancelled) setWebsiteVisitsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [websiteVisitsPage]);

  useEffect(() => {
    if (!selectedVisitorKey) {
      setWebsiteVisitDetail(null);
      setWebsiteVisitDetailError(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setWebsiteVisitDetailLoading(true);
        setWebsiteVisitDetailError(null);
        const data = await DashboardAPI.getWebsiteVisitDetail(selectedVisitorKey);
        if (cancelled) return;
        setWebsiteVisitDetail(data);
      } catch (err: any) {
        if (cancelled) return;
        setWebsiteVisitDetailError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load visitor detail.",
        );
      } finally {
        if (!cancelled) setWebsiteVisitDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedVisitorKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setEmailCapturesLoading(true);
        setEmailCapturesError(null);
        const data = await DashboardAPI.getEmailCaptures({
          page: emailCapturesPage,
          limit: 20,
        });
        if (!cancelled) setEmailCaptures(data);
      } catch (err: any) {
        if (!cancelled) {
          setEmailCapturesError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load captured emails.",
          );
        }
      } finally {
        if (!cancelled) setEmailCapturesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [emailCapturesPage]);

  return (
    <div
      style={{
        display: "grid",
        gap: 18,
        padding: isCompact ? 12 : 18,
        maxWidth: 1480,
        margin: "0 auto",
      }}
    >
      <section
        style={{
          ...cardStyle,
          padding: isCompact ? 18 : 24,
          display: "grid",
          gap: 18,
        }}
      >
        <SectionHeader
          title="Website Visit Explorer"
          subtitle="Cookie-backed visit tracking for guests plus account-linked browsing history for logged-in customers."
        />
      </section>

      <div
        style={{
          display: "grid",
          gap: 16,
          gridTemplateColumns: isCompact
            ? "minmax(0, 1fr)"
            : "minmax(0, 1.2fr) minmax(340px, 0.8fr)",
          alignItems: "start",
        }}
      >
        <section style={panelStyle}>
          <WebsiteVisitsTable
            data={websiteVisits}
            loading={websiteVisitsLoading}
            error={websiteVisitsError}
            selectedVisitorKey={selectedVisitorKey}
            search={visitorSearch}
            identityFilter={visitorIdentityFilter}
            onSearchChange={setVisitorSearch}
            onIdentityFilterChange={setVisitorIdentityFilter}
            onSelectVisitor={setSelectedVisitorKey}
            onPageChange={setWebsiteVisitsPage}
          />
        </section>

        <section style={panelStyle}>
          <WebsiteVisitDetailPanel
            detail={websiteVisitDetail}
            loading={websiteVisitDetailLoading}
            error={websiteVisitDetailError}
          />
        </section>
      </div>

      <section style={panelStyle}>
        <SectionHeader
          title="Email Offer Captures"
          subtitle="Emails submitted through the 10% first-order prompt and their conversion status."
        />
        <EmailCapturesPanel
          data={emailCaptures}
          loading={emailCapturesLoading}
          error={emailCapturesError}
          onPageChange={setEmailCapturesPage}
        />
      </section>
    </div>
  );
}
